/**
 * Sort array containing cells by rows and colums.
 * @param {number[]} cells - array that should be sorted.
 * @param {boolean} [isColFirst=true] - indicate which come first in paris, column or row.
 * @example
 *  let specialExpand = [2, 1, 1, 1, 2, 2, 1, 0, 3, 2, 1, 2, 0, 0];
 *  sortListOfCells(specialExpand);
 *  console.log(specialExpand);
 *  // now array `specialExpand` is sorted and looks like [0, 0, 1, 0, 1, 1, 2, 1, 1, 2, 2, 2, 3, 2]
 **/
export function sortCells(cells: Array<number>, isColFirst: boolean = true): Array<number> {
    const lastCellIndex = cells.length - 2;
    let cellIndex = 0,
        leftCol,
        leftRow,
        rightCol,
        rightRow,
        isLeftColBigger,
        isLeftRowBigger,
        isRowSame,
        shouldSwap,
        colIndexOffset,
        rowIndexOffset;

    if (isColFirst) {
        colIndexOffset = 0;
        rowIndexOffset = 1;
    } else {
        colIndexOffset = 1;
        rowIndexOffset = 0;
    }

    for (; cellIndex < lastCellIndex;) {
        leftCol = cells[cellIndex + colIndexOffset];
        leftRow = cells[cellIndex + rowIndexOffset];
        rightCol = cells[cellIndex + 2 + colIndexOffset];
        rightRow = cells[cellIndex + 2 + rowIndexOffset];
        isLeftRowBigger = leftRow > rightRow;
        isLeftColBigger = leftCol > rightCol;
        isRowSame = leftRow === rightRow;
        shouldSwap = isLeftRowBigger || (isRowSame && isLeftColBigger);

        if (shouldSwap) {
            cells.splice(cellIndex, 4, rightCol, rightRow, leftCol, leftRow);
            cellIndex = Math.max(0, cellIndex - 2);
        } else {
            cellIndex += 2;
        }
    }

    return cells;
}

/**
 * @param {number[]} cells - array of cell pairs that should be split into rows.
 * @param {boolean} [isColFirst=true] - indicate which come first in paris, column or row.
 * @param {boolean} [fillEmptyRows=true] - add empty row(null) if there is no cells in it.
 * @example
 *  const specialExpand = [1,0,1,1,2,1,1,2,2,2,3,2,2,5,4,6];
 *  splitIntoRows(specialExpand);              // [[1,0],[1,1,2,1],[1,2,2,2,3,2],null,null,[2,5],[4,6]]
 *  splitIntoRows(specialExpand, true, true);  // [[1,0],[1,1,2,1],[1,2,2,2,3,2],[2,5],[4,6]]
 **/
export function splitIntoRows(
    cells: Array<number>,
    isColFirst: boolean = true,
    fillEmptyRows: boolean = true
): Array<Array<number>> {
    let result: Array<Array<number>> = [];
    const lastCellIndex = cells.length;
    let cellIndex = 0,
        col,
        row,
        colIndexOffset,
        rowIndexOffset;

    if (isColFirst) {
        colIndexOffset = 0;
        rowIndexOffset = 1;
    } else {
        colIndexOffset = 1;
        rowIndexOffset = 0;
    }

    for (; cellIndex < lastCellIndex; cellIndex += 2) {
        col = cells[cellIndex + colIndexOffset];
        row = cells[cellIndex + rowIndexOffset];

        if (!result[row]) {
            result[row] = [];
        }

        if (isColFirst) {
            result[row].push(col, row);
        } else {
            result[row].push(row, col);
        }
    }

    if (!fillEmptyRows) {
        result = result.filter((row) => row);
    }

    return result;
}
